"use client";

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api";

// Create Context object
const AuthContext = createContext();

// ✅ Custom hook to use AuthContext easily
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// ✅ Provider that wraps the entire app
// This is where we store user & token globally
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);   // Store user details
  const [token, setToken] = useState(null); // Store JWT token
  const [loading, setLoading] = useState(true);

  const syncUserToStorage = useCallback((userData) => {
    if (!userData) return;
    localStorage.setItem("user", JSON.stringify(userData));
  }, []);

  const normalizeUserFromToken = useCallback((userData, authToken) => {
    if (!userData || !authToken) return userData;
    let mergedUser = { ...userData };
    try {
      const payload = JSON.parse(atob(authToken.split(".")[1]));
      if (payload?.branchcode && !mergedUser.branchcode) {
        mergedUser.branchcode = payload.branchcode;
      }
      if (Array.isArray(payload?.branchcodes) && !Array.isArray(mergedUser.branchcodes)) {
        mergedUser.branchcodes = payload.branchcodes;
      }
      if (payload?.roleid && !mergedUser.roleid) {
        mergedUser.roleid = payload.roleid;
      }
      if (Array.isArray(payload?.roleids) && !Array.isArray(mergedUser.roleids)) {
        mergedUser.roleids = payload.roleids;
      }
    } catch (_) {}
    return mergedUser;
  }, []);

  const ensureRoleName = useCallback(async (currentUser) => {
    if (!currentUser || !currentUser.roleid) return;
    if (currentUser.rolename) return;

    try {
      const response = await api.get("/api/user-role");
      const rolename = response?.data?.rolename;
      if (!rolename) return;

      const updatedUser = { ...currentUser, rolename };
      setUser(updatedUser);
      syncUserToStorage(updatedUser);
    } catch (_) {
      // ignore
    }
  }, [syncUserToStorage]);

  // ----------- Helper Functions ------------

  // Check if token is expired (decode JWT payload)
  const isTokenExpired = useCallback((token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime; // true if expired
    } catch (error) {
      return true;
    }
  }, []);

  // Extract token expiration timestamp
  const getTokenExpirationTime = useCallback((token) => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000; // Convert to ms
    } catch (error) {
      return null;
    }
  }, []);

  // ✅ Logout clears user + token from state & localStorage
  const logout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
    window.dispatchEvent(new CustomEvent("userLoggedOut"));
    window.location.href = "/"; // redirect to login
  }, []);

  // ✅ Frontshop Login stores user + token in state & localStorage
  const frontshopLogin = useCallback((userData, authToken) => {
    const mergedUser = normalizeUserFromToken(userData, authToken);
    localStorage.setItem("token", authToken);
    syncUserToStorage(mergedUser);
    setToken(authToken);
    setUser(mergedUser);
    void ensureRoleName(mergedUser);
  }, [ensureRoleName, normalizeUserFromToken, syncUserToStorage]);

  const switchBranch = useCallback(async (branchcode) => {
    const nextBranchCode = String(branchcode || "").trim();
    if (!nextBranchCode) return;
    const res = await api.post("/api/auth/switch-branch", { branchcode: nextBranchCode });
    const nextToken = res?.data?.token;
    const nextUser = res?.data?.user;
    if (!nextToken || !nextUser) return;
    const mergedUser = normalizeUserFromToken(nextUser, nextToken);
    localStorage.setItem("token", nextToken);
    syncUserToStorage(mergedUser);
    setToken(nextToken);
    setUser(mergedUser);
    void ensureRoleName(mergedUser);
  }, [ensureRoleName, normalizeUserFromToken, syncUserToStorage]);

  // ✅ Login stores user + token in state & localStorage
  const login = useCallback((userData, authToken) => {
    const mergedUser = normalizeUserFromToken(userData, authToken);
    localStorage.setItem("token", authToken);
    syncUserToStorage(mergedUser);
    setToken(authToken);
    setUser(mergedUser);
    void ensureRoleName(mergedUser);
  }, [ensureRoleName, normalizeUserFromToken, syncUserToStorage]);

  const switchRole = useCallback(async (roleid) => {
    const nextRoleId = Number(roleid);
    if (!Number.isInteger(nextRoleId)) return;
    const res = await api.post("/api/auth/switch-role", { roleid: nextRoleId });
    const nextToken = res?.data?.token;
    const nextUser = res?.data?.user;
    if (!nextToken || !nextUser) return;
    const mergedUser = normalizeUserFromToken(nextUser, nextToken);
    localStorage.setItem("token", nextToken);
    syncUserToStorage(mergedUser);
    setToken(nextToken);
    setUser(mergedUser);
    void ensureRoleName(mergedUser);
  }, [ensureRoleName, normalizeUserFromToken, syncUserToStorage]);

  const updateUser = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      syncUserToStorage(next);
      return next;
    });
  }, [syncUserToStorage]);

  // ✅ Check token expiration regularly
  const checkTokenExpiration = useCallback(() => {
    const currentToken = localStorage.getItem("token");
    if (!currentToken) return;

    const expirationTime = getTokenExpirationTime(currentToken);
    if (!expirationTime || expirationTime <= Date.now()) {
      logout(); // force logout if expired
      return;
    }

    const timeUntilExpiry = expirationTime - Date.now();

    if (timeUntilExpiry <= 120000) {
      // Fire warning event if token expires in <= 2 minutes
      window.dispatchEvent(
        new CustomEvent("tokenExpiring", { detail: { timeUntilExpiry } })
      );
    }
  }, [getTokenExpirationTime, logout]);

  // ----------- Effects ------------

  // On mount, restore user & token from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");

    if (storedToken && storedUser && !isTokenExpired(storedToken)) {
      let parsedUser = {};
      try { parsedUser = JSON.parse(storedUser) || {}; } catch { parsedUser = {}; }
      parsedUser = normalizeUserFromToken(parsedUser, storedToken);
      try { localStorage.setItem("user", JSON.stringify(parsedUser)); } catch {}
      setToken(storedToken);
      setUser(parsedUser);
      void ensureRoleName(parsedUser);
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    }

    setLoading(false);
  }, [ensureRoleName, isTokenExpired, normalizeUserFromToken]);

  // Re-check token every 10 seconds
  useEffect(() => {
    let intervalId;
    if (token && !isTokenExpired(token)) {
      intervalId = setInterval(() => {
        checkTokenExpiration();
      }, 10000);
    }
    return () => clearInterval(intervalId);
  }, [token, isTokenExpired, checkTokenExpiration]);

  // Handle logout & tokenExpired events globally
  useEffect(() => {
    const handleTokenExpired = () => logout();
    const handleUserLoggedOut = () => {
      setUser(null);
      setToken(null);
    };

    window.addEventListener("tokenExpired", handleTokenExpired);
    window.addEventListener("userLoggedOut", handleUserLoggedOut);

    return () => {
      window.removeEventListener("tokenExpired", handleTokenExpired);
      window.removeEventListener("userLoggedOut", handleUserLoggedOut);
    };
  }, [logout]);

  // ----------- Context Value ------------
  const value = {
    user,
    token,
    loading,
    login,     // function to log in user
    frontshopLogin, // function to log in frontshop user
    switchRole,
    switchBranch,
    updateUser,
    logout,    // function to log out user
    isAuthenticated: !!token && !!user && !isTokenExpired(token), // check if user is logged in
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
